/**
 * Alerts Option A ear: hold-to-listen (ENG-2136 transport via the live player).
 * Presses register on listenInHoldStore; LiveStreamPlayer unmutes while held.
 * Only shown on Live Stream / Split View (hidden on Alert Clip).
 */
import React from "react";
import { useIsTalkingToCamera } from "../../common/TalkDown/talkingCamerasStore";
import { liveMediaNeedsProductionMode } from "../../utils/productionMode";
import { useIsStaffUser } from "../../utils/staffUser";
import {
  MUTED_WHILE_TALKING_MESSAGE,
  useListenInEnabled,
} from "./ListenInButton";
import {
  useListenInHasAudio,
  useListenInHoldStore,
} from "./listenInHoldStore";

export const ALERT_LISTEN_IN_LABEL = "Hold to listen in";
export const ALERT_LISTEN_IN_UNAVAILABLE_LABEL =
  "Listen-in isn't available for this camera";
/** Staging build with Use production off: the live player never plays here. */
export const ALERT_LISTEN_IN_NEEDS_PRODUCTION_MODE_STAFF_LABEL =
  "Listen-in uses real cameras. Switch on Use production to try it.";
export const ALERT_LISTEN_IN_NEEDS_PRODUCTION_MODE_LABEL =
  "Listen-in isn't available on this test site.";

type ListenInViewMode = "clip" | "live" | "split";

const idleButtonClassName =
  "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] border-[1.5px] border-[#cfd8e6] bg-white text-[#233762] disabled:cursor-default";
const unavailableButtonClassName =
  "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] border-[1.5px] border-[#e2e8f2] bg-[#f5f7fa] text-[#9AA3B5] disabled:cursor-default";
const activeButtonClassName =
  "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] border-[1.5px] border-actuate-orange bg-actuate-orange text-white";

const EarIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 8.5a6.5 6.5 0 1 1 13 0c0 6-6 6-6 10a3.5 3.5 0 1 1-7 0" />
    <path d="M15 8.5a2.5 2.5 0 0 0-5 0v1a2 2 0 1 1 0 4" />
  </svg>
);

/** Ear with a slash — used when listen-in isn't available. */
const EarUnavailableIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 8.5a6.5 6.5 0 1 1 13 0c0 6-6 6-6 10a3.5 3.5 0 1 1-7 0" />
    <path d="M15 8.5a2.5 2.5 0 0 0-5 0v1a2 2 0 1 1 0 4" />
    <line x1="3" y1="3" x2="21" y2="21" />
  </svg>
);

const isActivationKey = (key: string) => key === " " || key === "Enter";

export interface AlertListenInControlProps {
  cameraId: string | number | null | undefined;
  viewMode: ListenInViewMode;
}

export const AlertListenInControl = ({
  cameraId,
  viewMode,
}: AlertListenInControlProps) => {
  const listenInEnabled = useListenInEnabled();
  const hasAudio = useListenInHasAudio(cameraId);
  const talking = useIsTalkingToCamera(cameraId);
  const setHold = useListenInHoldStore((s) => s.setHold);
  const holding = useListenInHoldStore(
    (s) =>
      cameraId != null &&
      cameraId !== "" &&
      s.holdCameraId === String(cameraId)
  );
  const heldKeyRef = React.useRef(false);
  const staff = useIsStaffUser();
  const cameraKey =
    cameraId != null && cameraId !== "" ? String(cameraId) : null;

  const liveReady = viewMode === "live" || viewMode === "split";
  // Crossed out when listen-in isn't available:
  // - showListenIn flag off
  // - no camera
  // - talking down (half-duplex)
  // - player/mint reported no audio (hasAudio === false)
  // Until a player reports, stay optimistic so we don't flash unavailable
  // while the live stream is still starting.
  // - staging build with Use production off: the player can't play, so the
  //   optimistic ear would otherwise show and silently do nothing
  const needsProductionMode = liveMediaNeedsProductionMode();
  const unavailable =
    !listenInEnabled ||
    !cameraKey ||
    talking ||
    hasAudio === false ||
    needsProductionMode;
  const canHold = !unavailable;

  const clearHoldIfOurs = React.useCallback(() => {
    if (!cameraKey) return;
    const current = useListenInHoldStore.getState().holdCameraId;
    if (current === cameraKey) {
      useListenInHoldStore.getState().setHold(null);
    }
  }, [cameraKey]);

  React.useEffect(() => {
    if (!liveReady) clearHoldIfOurs();
  }, [liveReady, clearHoldIfOurs]);

  React.useEffect(() => {
    return () => clearHoldIfOurs();
  }, [clearHoldIfOurs]);

  const startHold = () => {
    if (!canHold || !cameraKey || !liveReady) return;
    setHold(cameraKey);
  };

  const endHold = () => {
    clearHoldIfOurs();
  };

  if (!liveReady) return null;

  if (!canHold) {
    const label = talking
      ? MUTED_WHILE_TALKING_MESSAGE
      : needsProductionMode && listenInEnabled && cameraKey
      ? staff
        ? ALERT_LISTEN_IN_NEEDS_PRODUCTION_MODE_STAFF_LABEL
        : ALERT_LISTEN_IN_NEEDS_PRODUCTION_MODE_LABEL
      : ALERT_LISTEN_IN_UNAVAILABLE_LABEL;
    return (
      <button
        type="button"
        aria-label={label}
        title={label}
        className={unavailableButtonClassName}
        disabled
      >
        <EarUnavailableIcon />
      </button>
    );
  }

  return (
    <button
      type="button"
      aria-label={ALERT_LISTEN_IN_LABEL}
      aria-pressed={holding}
      title={ALERT_LISTEN_IN_LABEL}
      onPointerDown={(event) => {
        if (event.button !== undefined && event.button !== 0) return;
        event.preventDefault();
        startHold();
      }}
      onPointerUp={endHold}
      onPointerLeave={() => {
        if (holding) endHold();
      }}
      onPointerCancel={endHold}
      onKeyDown={(event) => {
        if (!isActivationKey(event.key) || event.repeat) return;
        event.preventDefault();
        heldKeyRef.current = true;
        startHold();
      }}
      onKeyUp={(event) => {
        if (!isActivationKey(event.key) || !heldKeyRef.current) return;
        heldKeyRef.current = false;
        endHold();
      }}
      onBlur={() => {
        if (holding) endHold();
      }}
      onContextMenu={(event) => event.preventDefault()}
      style={{ touchAction: "none", userSelect: "none" }}
      className={holding ? activeButtonClassName : idleButtonClassName}
    >
      <EarIcon />
    </button>
  );
};
